import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { c as classNames, b as EGDSContextWrapper, l as liveAnnounce } from '../theme-provider/index-chunk.esm.js';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { E as EGDSField } from '../field-message/index-chunk.esm.js';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
import styles from '../styles/uitk-egds-input.css';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';
import useForkRef from '@rooks/use-fork-ref';
import '../paging-button/index-chunk.esm.js';
import { E as EGDSSecondaryButton } from '../secondary-button/index-chunk.esm.js';

const getClassNameList$9 = classNames.bind(styles);
class Input extends React.Component {
    static defaultProps = {
        hasClearButton: false,
        required: false,
        // eslint-disable-next-line react/default-props-match-prop-types -- Applied to avoid a breaking change. Must be fixed ASAP with https://github.expedia.biz/egds/uitk-react/issues/6520
        value: '',
    };
    autofillInput;
    inputRef;
    id; // Unique ID if not provided for label a11y
    constructor(props) {
        super(props);
        this.state = {
            autofilled: false,
            mounted: false,
        };
        this.handleClear = this.handleClear.bind(this);
        this.setAutoFill = this.setAutoFill.bind(this);
        this.inputRef = props.domRef || React.createRef();
        this.id = props.id ?? `input-${Math.random().toString(36).substring(2, 9)}` ?? undefined; // Can replace with useId when upgrading to React 18
    }
    componentDidMount() {
        this.startTimer();
        this.setState({ mounted: true });
    }
    componentWillUnmount() {
        const { domRef } = this.props;
        if (domRef) {
            this.clearTimer();
        }
    }
    handleClear() {
        const { handleClear, onChange, name } = this.props;
        if (onChange) {
            onChange({
                target: { value: '', name: name ?? this.id },
            });
            // Shift focus back to input field for a11y
            this.inputRef?.current?.focus();
        }
        if (typeof handleClear === 'function') {
            handleClear();
        }
    }
    setAutoFill() {
        const curr = this.inputRef?.current;
        if (curr) {
            try {
                if (curr.matches(':autofill')) {
                    this.clearTimer();
                    this.setState({ autofilled: true });
                }
            }
            catch (error) {
                try {
                    if (curr.matches(':-webkit-autofill')) {
                        this.clearTimer();
                        this.setState({ autofilled: true });
                    }
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                }
                catch (error) {
                    return false;
                }
            }
        }
    }
    startTimer() {
        // Before init setTimeout, clear the existing timer
        this.clearTimer();
        this.autofillInput = setTimeout(() => {
            this.setAutoFill();
        }, 500);
    }
    clearTimer() {
        clearTimeout(this.autofillInput);
    }
    render() {
        // Black list these props from the input field
        const { children, className, label, hasBorder, hasFloatedLabel, hasVisibleLabel, icon, instruction, invalid, l10n, hasClearButton, required, clearButtonLabel, ...attributes } = this.props;
        const { placeholder, ...attrs } = attributes;
        const { disabled, readOnly, value } = attributes;
        const { autofilled, mounted } = this.state;
        const showClearButton = mounted && hasClearButton && value;
        const fieldPropsClassNames = getClassNameList$9(showClearButton && 'has-input-cta');
        const fieldProps = {
            labelClassName: fieldPropsClassNames,
            disabled,
            hasAutofill: autofilled,
            hasBorder,
            hasFloatedLabel,
            hasVisibleLabel,
            icon,
            id: this.id,
            instruction,
            invalid,
            label,
            placeholder,
            readOnly,
            required,
        };
        const inputExtraAttr = attrs;
        if (inputExtraAttr.domRef) {
            delete inputExtraAttr.domRef;
        }
        if (inputExtraAttr.handleClear) {
            delete inputExtraAttr.handleClear;
        }
        const cssClassNames = getClassNameList$9('uitk-field-input', showClearButton && 'has-input-cta', className, !placeholder && !value?.toString() && 'empty-placeholder');
        let ariaFieldLabel = clearButtonLabel || label;
        if (React.isValidElement(label)) {
            const labelProp = label.props;
            ariaFieldLabel = labelProp && labelProp.children && labelProp.children[0];
        }
        const localizedClearText = l10n.formatText('clear');
        const iconTitle = [localizedClearText, ariaFieldLabel].join(' ');
        return (jsxs(EGDSField, { ...fieldProps, children: [jsx("input", { ...inputExtraAttr, className: cssClassNames, "aria-describedby": invalid ? `${this.id}-error` : undefined, placeholder: placeholder, ref: this.inputRef, id: this.id, "aria-required": required, "aria-invalid": !!invalid }), children, showClearButton && mounted && (jsx("button", { className: getClassNameList$9('uitk-clear-button', 'uitk-input-cta'), onClick: this.handleClear, type: "button", children: jsx(EGDSIcon, { name: "cancel", size: EGDSIconSize.SMALL, title: iconTitle, id: `${this.id}-clear-input-button` }) }))] }));
    }
}
const EGDSInput = (props) => {
    const l10n = useLocalization();
    return jsx(Input, { l10n: l10n, ...props });
};

const getClassNameList$8 = classNames.bind(styles);
class FauxInput extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            mounted: false,
        };
        this.handleClear = this.handleClear.bind(this);
    }
    componentDidMount() {
        this.setState({ mounted: true });
    }
    handleClear() {
        const { handleClear } = this.props;
        if (typeof handleClear === 'function') {
            handleClear();
        }
    }
    render() {
        const { mounted } = this.state;
        const { className, domRef, children, analytics, hasClearButton, label, l10n, ...attrs } = this.props;
        const showClearButton = mounted && hasClearButton && children;
        // Dummy button is needed to re-render the faux input especially on Firefox for cases where clear button is not required
        const showDummyButton = mounted && !hasClearButton && children;
        const cssClassNames = getClassNameList$8('uitk-faux-input', className);
        const iconTitle = [l10n.formatText('clear'), label].join(' ');
        if (attrs.handleClear) {
            delete attrs.handleClear;
        }
        return (jsx(EGDSContextWrapper, { children: (context, data) => (jsxs(Fragment, { children: [jsx("button", { ...attrs, type: "button", className: getClassNameList$8(cssClassNames, context === 'uitk-field-context' && 'uitk-field-faux-input', data?.icon && 'uitk-field-faux-input-hasicon', data?.invalid && 'uitk-field-faux-input-invalid'), ref: domRef, onClickCapture: analytics && analyticsTracker.bind(this, analytics), children: children }), showDummyButton && (jsx("button", { className: getClassNameList$8('is-visually-hidden'), type: "button", "aria-hidden": "true", tabIndex: -1 })), showClearButton && (jsx("button", { className: getClassNameList$8('uitk-clear-button', 'uitk-input-cta'), type: "button", onClick: this.handleClear, children: jsx(EGDSIcon, { name: "cancel", size: EGDSIconSize.SMALL, id: "faux-input-clear-button", title: iconTitle }) }))] })) }));
    }
}
const EGDSFauxInput = (props) => {
    const l10n = useLocalization();
    return jsx(FauxInput, { l10n: l10n, ...props });
};

class EGDSTextInput extends React.Component {
    static displayName = 'EGDSTextInput';
    render() {
        return jsx(EGDSInput, { ...this.props, type: "text" });
    }
}

const getClassNameList$7 = classNames.bind(styles);
class PasswordInput extends React.Component {
    static displayName = 'EGDSPasswordInput';
    constructor(props) {
        super(props);
        const { passwordVisible } = this.props;
        this.state = {
            passwordVisible,
        };
        this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
    }
    componentDidUpdate(prevProps) {
        const { passwordVisible } = this.props;
        if (prevProps.passwordVisible !== passwordVisible) {
            this.setState({ passwordVisible });
        }
    }
    togglePasswordVisibility() {
        const { passwordVisible } = this.state;
        const isPasswordVisible = !passwordVisible;
        this.setState({ passwordVisible: isPasswordVisible });
    }
    render() {
        const { l10n } = this.props;
        const { passwordVisible } = this.state;
        return (jsx(EGDSInput, { ...this.props, type: passwordVisible ? 'text' : 'password', hasClearButton: false, className: getClassNameList$7('uitk-password-visibility-input', 'has-input-cta'), children: jsxs("button", { className: getClassNameList$7('uitk-password-visibility-button', 'uitk-input-cta'), onClick: this.togglePasswordVisibility, type: "button", "aria-label": passwordVisible ? l10n.formatText('password.hide') : l10n.formatText('password.show'), children: [jsx("span", { className: getClassNameList$7('is-visually-hidden'), children: passwordVisible ? l10n.formatText('password.hide') : l10n.formatText('password.show') }), passwordVisible ? (jsx(EGDSIcon, { name: "visibility_off", size: EGDSIconSize.SMALL })) : (jsx(EGDSIcon, { name: "visibility", size: EGDSIconSize.SMALL }))] }) }));
    }
}
const EGDSPasswordInput = (props) => {
    const l10n = useLocalization();
    return jsx(PasswordInput, { l10n: l10n, ...props });
};

const getClassNameList$6 = classNames.bind(styles);
const RenderInformativeTrigger = (props) => {
    const { trigger, a11yLabel } = props;
    const childrenArray = React.Children.toArray(trigger);
    const firstChild = childrenArray[0];
    let triggerClassName = '';
    if (firstChild.props.className) {
        triggerClassName = firstChild.props.className;
    }
    const triggerClassNames = getClassNameList$6('uitk-informative-input-trigger', 'uitk-input-cta', triggerClassName);
    return (jsx(Fragment, { children: React.cloneElement(trigger, {
            'aria-label': a11yLabel,
            className: triggerClassNames,
        }, jsx(EGDSIcon, { name: "info_outline", size: EGDSIconSize.SMALL })) }));
};
class InformativeInput extends React.Component {
    render() {
        const { informativeTrigger, a11yInformativeTrigger, ...childProps } = this.props;
        return (jsx(EGDSInput, { ...childProps, className: getClassNameList$6('has-input-cta'), type: "text", children: jsx(RenderInformativeTrigger, { trigger: informativeTrigger, a11yLabel: a11yInformativeTrigger }) }));
    }
}
const EGDSInformativeInput = (props) => {
    const l10n = useLocalization();
    return jsx(InformativeInput, { l10n: l10n, ...props });
};

const getClassNameList$5 = classNames.bind(styles);
const ownerDocument = (node) => (node && node.ownerDocument) || document;
const ownerWindow = (node) => {
    const doc = ownerDocument(node);
    return doc.defaultView || window;
};
const getStyleValue = (computedStyle, property) => parseInt(computedStyle[property], 10) || 0;
const shadowStyles = {
    height: 0,
    left: 0,
    minHeight: 'unset',
    // Ignore the scrollbar width
    overflow: 'hidden',
    // Create a new layer, increase the isolation of the computed values
    // Remove from the content flow
    position: 'absolute',
    top: 0,
    transform: 'translateZ(0)',
    // Visibility needed to hide the extra text area on iPads
    visibility: 'hidden',
};
const useEnhancedEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;
const EGDSTextArea = React.forwardRef((props, ref) => {
    const { onChange, maxRows, minRows = 2, value, className, disabled, hasBorder, hasFloatedLabel, hasVisibleLabel, id, instruction, invalid, label, placeholder, readOnly, required, domRef, ...other } = props;
    const uitkFieldProps = {
        disabled,
        hasBorder,
        hasFloatedLabel,
        hasVisibleLabel,
        id,
        instruction,
        invalid,
        label,
        placeholder,
        readOnly,
        required,
    };
    const { current: isControlled } = React.useRef(value !== null);
    const inputRef = React.useRef(null);
    // Giving priority to domRef as the forwarded ref since it is documented
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const handleRef = domRef ? useForkRef(domRef, inputRef) : useForkRef(ref, inputRef);
    const shadowRef = React.useRef(null);
    const renders = React.useRef(0);
    const [state, setState] = React.useState({});
    const textAreaClassNames = getClassNameList$5('uitk-field-textarea', className);
    const syncHeight = React.useCallback(() => {
        const input = inputRef.current;
        const inputShallow = shadowRef.current;
        if (input && inputShallow) {
            const containerWindow = ownerWindow(input);
            const computedStyle = containerWindow.getComputedStyle(input);
            inputShallow.value = input.value || placeholder || 'x';
            const boxSizing = computedStyle['box-sizing'];
            const padding = getStyleValue(computedStyle, 'padding-bottom') + getStyleValue(computedStyle, 'padding-top');
            const border = getStyleValue(computedStyle, 'border-bottom-width') + getStyleValue(computedStyle, 'border-top-width');
            // The height of the inner content
            const innerHeight = inputShallow.scrollHeight - padding;
            // Measure height of a textarea with a single row
            inputShallow.value = 'x';
            const singleRowHeight = inputShallow.scrollHeight - padding;
            // The height of the outer content
            let outerHeight = innerHeight;
            if (minRows) {
                outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
            }
            if (maxRows) {
                outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
            }
            outerHeight = Math.max(outerHeight, singleRowHeight);
            // Take the box sizing into account for applying this value as a style.
            const outerHeightStyle = outerHeight + (boxSizing === 'border-box' ? padding + border : 0) + singleRowHeight / 2;
            const overflow = Math.abs(outerHeight - innerHeight) <= 1;
            setState((prevState) => {
                // Need a large enough difference to update the height.
                // This prevents infinite rendering loop.
                if (renders.current < 20 &&
                    ((outerHeightStyle > 0 && Math.abs((prevState.outerHeightStyle || 0) - outerHeightStyle) > 1) ||
                        prevState.overflow !== overflow)) {
                    renders.current += 1;
                    return {
                        outerHeightStyle,
                        overflow,
                    };
                }
                return prevState;
            });
        }
    }, [maxRows, minRows, placeholder]);
    useEnhancedEffect(() => {
        syncHeight();
    });
    React.useEffect(() => {
        renders.current = 0;
    }, [value]);
    const handleChange = (event) => {
        renders.current = 0;
        if (!isControlled) {
            syncHeight();
        }
        if (onChange) {
            onChange(event);
        }
    };
    return (jsxs(EGDSField, { ...uitkFieldProps, children: [jsx("textarea", { "aria-required": required, "aria-invalid": !!invalid, className: getClassNameList$5(textAreaClassNames, { [`empty-placeholder`]: !value && !placeholder }), id: id, "aria-describedby": invalid ? `${id}-error` : undefined, value: value, onChange: handleChange, placeholder: placeholder, ref: handleRef, rows: minRows, style: {
                    height: state.outerHeightStyle,
                    overflow: state.overflow ? 'hidden' : undefined,
                }, ...other, disabled: disabled, readOnly: readOnly }), jsx("textarea", { "aria-hidden": true, className: textAreaClassNames, placeholder: placeholder, readOnly: true, ref: shadowRef, tabIndex: -1, style: { ...shadowStyles }, "data-test-id": other['data-test-id'] ? `${other['data-test-id']}-hidden` : undefined, disabled: disabled })] }));
});
EGDSTextArea.defaultProps = {
    required: false,
};
EGDSTextArea.displayName = 'EGDSTextArea';

const getClassNameList$4 = classNames.bind(styles);
const EGDSChatTextArea = (props) => {
    const { ...remaining } = props;
    return (jsx(EGDSTextArea, { className: getClassNameList$4('uitk-field-textarea-with-chat'), ...remaining, hasVisibleLabel: false }));
};
EGDSChatTextArea.displayName = 'EGDSChatTextArea';

const getClassNameList$3 = classNames.bind(styles);
class FakeInput extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            mounted: false,
        };
        this.getPlaceholderText = this.getPlaceholderText.bind(this);
        this.handleClear = this.handleClear.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }
    componentDidMount() {
        this.setState({ mounted: true });
    }
    handleChange(e) {
        const { onChange } = this.props;
        if (onChange) {
            onChange(e);
        }
    }
    handleClear() {
        const { handleClear } = this.props;
        if (typeof handleClear === 'function') {
            handleClear();
        }
    }
    getPlaceholderText() {
        const { props } = this;
        let result = '';
        if (props.placeholder) {
            result = props.placeholder;
        }
        else if (props.value) {
            // Uitk-field needs some placeholder as default to place label properly
            result = 'Placeholder';
        }
        return result;
    }
    render() {
        const { mounted } = this.state;
        const { className, domRef, analytics, hasClearButton, icon, trailingIcon, id, invalid, label, l10n, placeholder, required, value, trailingText, clearButtonLabel, ...attrs } = this.props;
        const showClearButton = mounted && hasClearButton && value;
        const buttonClassNames = getClassNameList$3(className, 'uitk-fake-input', 'uitk-form-field-trigger');
        const inputClassNames = getClassNameList$3({ 'is-hidden': mounted });
        const iconTitle = [l10n.formatText('clear'), clearButtonLabel || label].join(' ');
        if (attrs.handleClear) {
            delete attrs.handleClear;
        }
        const ariaLabelParts = [];
        // Whether or not this input is required
        if (required)
            ariaLabelParts[0] = l10n.formatText('fake.input.required');
        // Provided aria-label or just the already visible label
        ariaLabelParts[1] = attrs['aria-label'] ?? label;
        // Visible parts of label (value/placeholder followed by optional trailing text)
        ariaLabelParts[2] = `${value || placeholder || ''}${trailingText && value ? ` ${trailingText}` : ''}`;
        return (jsx(EGDSTextInput, { className: inputClassNames, id: id, invalid: invalid, label: label, required: required, onChange: this.handleChange, placeholder: this.getPlaceholderText(), value: value || '', icon: icon, children: mounted && (jsx(EGDSContextWrapper, { children: (context, data) => (jsxs(Fragment, { children: [jsxs("button", { ...attrs, "aria-label": ariaLabelParts.filter((str) => str.length > 0).join(', '), className: getClassNameList$3(buttonClassNames, context === 'uitk-field-context' && 'uitk-field-fake-input', data.icon && 'uitk-field-fake-input-hasicon', data.invalid && 'uitk-field-fake-input-invalid'), onClickCapture: analytics && analyticsTracker.bind(this, analytics), ref: domRef, type: "button", children: [value || placeholder, trailingText && value && ` ${trailingText}`, trailingIcon && (jsx(EGDSIcon, { name: trailingIcon, className: getClassNameList$3(`uitk-fake-input-right-icon`), size: EGDSIconSize.SMALL }))] }), showClearButton && (jsx("button", { className: getClassNameList$3('uitk-clear-button', 'uitk-input-cta'), onClick: this.handleClear, type: "button", children: jsx(EGDSIcon, { id: "fake-input-clear-button", name: "cancel", size: EGDSIconSize.SMALL, title: iconTitle }) }))] })) })) }));
    }
}
const EGDSFakeInput = (props) => {
    const l10n = useLocalization();
    return jsx(FakeInput, { l10n: l10n, ...props });
};

const getClassNameList$2 = classNames.bind(styles);
const EGDSCardNumberInput = (props) => {
    const { children, ...remaining } = props;
    return (jsx(EGDSInput, { ...remaining, type: "text", children: jsx("span", { className: getClassNameList$2('uitk-card-number-input-payment-type'), children: children }) }));
};
EGDSCardNumberInput.displayName = 'EGDSCardNumberInput';

const getClassNameList$1 = classNames.bind(styles);
class EGDSSelect extends React.Component {
    static displayName = 'EGDSSelect';
    static defaultProps = {
        emptyState: false,
    };
    constructor(props) {
        super(props);
        this.state = {
            emptyStateSelection: false,
        };
        this.handleChange = this.handleChange.bind(this);
    }
    handleEmptyState() {
        const { emptyState } = this.props;
        if (emptyState) {
            this.setState(() => ({ emptyStateSelection: true }));
        }
    }
    handleChange = (event) => {
        const { placeholder, onChange, onBlur } = this.props;
        if (event.type === 'change') {
            this.handleEmptyState();
            if (onChange) {
                onChange(event);
            }
        }
        else if (event.type === 'blur' && onBlur) {
            onBlur(event);
        }
        if (placeholder && event.target && event.target.value !== placeholder) {
            event.target.classList.remove('uitk-field-select-placeholder');
        }
    };
    render() {
        // Black list these props from the select field
        const { className, domRef, label, hasBorder, hasFloatedLabel, hasVisibleLabel, icon, invalid, placeholder, children, emptyState, ...attributes } = this.props;
        const { id, disabled, readOnly, value } = attributes;
        const { emptyStateSelection } = this.state;
        const baseClass = 'uitk-field-select';
        const defaultValue = emptyState ? '' : placeholder;
        const fieldProps = {
            className: getClassNameList$1(`${baseClass}-field`, emptyState && !value && !emptyStateSelection && `${baseClass}-empty-state`),
            disabled,
            hasBorder,
            hasFloatedLabel,
            hasVisibleLabel,
            icon,
            id,
            invalid,
            label,
            placeholder: defaultValue,
            readOnly,
            required: attributes.required,
        };
        const wrapperClassNames = getClassNameList$1(baseClass, !emptyState && placeholder && `${baseClass}-placeholder`, className);
        const iconClassNames = getClassNameList$1(`${baseClass}-icon`, readOnly && `${baseClass}-icon-read-only`);
        return (jsxs(EGDSField, { ...fieldProps, children: [jsxs("select", { ...attributes, className: wrapperClassNames, "aria-describedby": invalid ? `${id}-error` : undefined, ref: domRef, defaultValue: defaultValue, onChange: this.handleChange, onBlur: this.handleChange, children: [(emptyState || placeholder) && (jsx("option", { value: defaultValue, disabled: true, className: getClassNameList$1(`${baseClass}-dummy-option`), "aria-selected": "false", children: defaultValue })), children] }), jsx(EGDSIcon, { name: "expand_more", className: iconClassNames, size: EGDSIconSize.SMALL })] }));
    }
}

const getClassNameList = classNames.bind(styles);
const EGDSInputSwapper = (props) => {
    const { startInput, endInput, onSwapButtonClick, isHorizontalSwapper = true, a11ySwapButtonLabel, id, onSwapClickAnnounceText, } = props;
    const uid = Math.random().toString(36).substring(7);
    const { formatText } = useLocalization();
    const { label: startInputLabel, value: startInputValue } = startInput.props;
    const { label: endInputLabel, value: endInputValue } = endInput.props;
    const [toggleRotateClassName, setToggleRotateClassName] = React.useState('');
    const [toggle, setToggle] = React.useState(true);
    const buttonRef = React.useRef(null);
    const baseClassName = 'uitk-input-swapper';
    const wrapperClassnames = getClassNameList(baseClassName, `${baseClassName}-horizlayout-${isHorizontalSwapper}`);
    const toggleClassnames = getClassNameList(`${baseClassName}-toggle`, toggleRotateClassName);
    const toggleIconClassnames = getClassNameList(`${baseClassName}-toggle-icon`);
    const startInputClassnames = getClassNameList(`${baseClassName}-start-input`);
    const endInputClassnames = getClassNameList(`${baseClassName}-end-input`);
    const swapButtonAction = formatText('input.swapper.action', startInputLabel, endInputValue, endInputLabel, startInputValue);
    const swapButtonInstructions = formatText('input.swapper.instructions', startInputLabel, endInputLabel);
    const swapInputFields = () => {
        setToggle((prevState) => {
            if (toggle) {
                setToggleRotateClassName(`${baseClassName}-toggle-rotate-forwards`);
            }
            else {
                setToggleRotateClassName(`${baseClassName}-toggle-rotate-backwards`);
            }
            liveAnnounce(`${onSwapClickAnnounceText ?? swapButtonAction}`);
            return !prevState;
        });
        if (onSwapButtonClick) {
            onSwapButtonClick();
        }
    };
    return (jsxs("div", { className: wrapperClassnames, children: [jsx("div", { className: startInputClassnames, children: startInput }), jsx(EGDSSecondaryButton, { onClick: swapInputFields, "aria-label": a11ySwapButtonLabel ?? swapButtonInstructions, className: toggleClassnames, domRef: buttonRef, children: jsx(EGDSIcon, { className: toggleIconClassnames, name: "swap_vert", title: a11ySwapButtonLabel ?? swapButtonInstructions, id: `swap-horizontal-icon-${id ?? uid}` }) }), jsx("div", { className: endInputClassnames, children: endInput })] }));
};

export { EGDSInput as E, RenderInformativeTrigger as R, EGDSFauxInput as a, EGDSTextInput as b, EGDSPasswordInput as c, EGDSInformativeInput as d, EGDSTextArea as e, EGDSChatTextArea as f, EGDSFakeInput as g, EGDSCardNumberInput as h, EGDSSelect as i, EGDSInputSwapper as j };
//# sourceMappingURL=index-chunk.esm.js.map
